import React, { useState } from "react";
import { Button, Form, Space, Typography, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { createMerchant, updateMerchant, type MerchantType } from "../../redux/MerchantsUser";
import { MerchantBoxProps, Merchant } from "./types";
import {
  StyledModal,
  BackButton,
  SaveButton,
  FormButtonContainer,
  StyledInput,
  StyledTextArea,
  StyledPasswordInput,
  StyledForm,
} from "./styles";

const { Title } = Typography;

const MerchantBox: React.FC<MerchantBoxProps> = ({
  initialMerchant,
  onSave,
  onCancel,
  fetchMerchants,
  isManagerMode = false,
  isStaffMode = false,
}) => {
  const [form] = Form.useForm();
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.merchants);

  const isEditMode = !!initialMerchant;
  const merchantType: MerchantType = isManagerMode
    ? "manager"
    : isStaffMode
    ? "staff"
    : "merchant";

  React.useEffect(() => {
    if (initialMerchant) {
      form.setFieldsValue({
        name: initialMerchant.name,
        username: initialMerchant.username,
        email: initialMerchant.email,
        phone: initialMerchant.phone,
        address: initialMerchant.address,
      });
    } else {
      form.resetFields();
    }
  }, [initialMerchant, form]);

  const handleSave = async (
    values: Omit<Merchant, "id" | "password"> & { password?: string }
  ) => {
    try {
      if (isEditMode && initialMerchant) {
        const result = await dispatch(
          updateMerchant({
            type: merchantType,
            payload: {
              id: initialMerchant.id,
              name: values.name,
              username: values.username,
              email: values.email,
              phone: values.phone,
              address: values.address,
            },
          })
        );

        if (updateMerchant.fulfilled.match(result)) {
          message.success(
            `${
              isManagerMode
                ? "Merchant Manager"
                : isStaffMode
                ? "Staff"
                : "Merchant"
            } updated successfully.`
          );
          fetchMerchants();
          onSave();
        } else {
          const errorMsg =
            typeof result.payload === "string"
              ? result.payload
              : "Update failed";
          message.error(errorMsg);
        }
      } else {
        const result = await dispatch(
          createMerchant({
            type: merchantType,
            payload: {
              name: values.name,
              username: values.username,
              email: values.email,
              phone: values.phone,
              address: values.address,
              password: values.password,
            },
          })
        );

        if (createMerchant.fulfilled.match(result)) {
          message.success(
            `${
              isManagerMode
                ? "Merchant Manager"
                : isStaffMode
                ? "Staff"
                : "Merchant"
            } created successfully.`
          );
          fetchMerchants();
          onSave();
        } else {
          const errorMsg =
            typeof result.payload === "string"
              ? result.payload
              : "Creation failed";
          message.error(errorMsg);
        }
      }
      setIsUnsavedChanges(false);
    } catch (error: any) {
      message.error(error.message || "An error occurred");
    }
  };

  const handleCancel = () => {
    if (isUnsavedChanges) {
      setIsCancelModalOpen(true);
    } else {
      onCancel();
    }
  };

  const confirmCancel = () => {
    setIsUnsavedChanges(false);
    form.resetFields();
    onCancel();
    setIsCancelModalOpen(false);
  };

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space>
          <BackButton icon={<ArrowLeftOutlined />} onClick={handleCancel}>
            Back
          </BackButton>
          <Title level={4} style={{ margin: 0 }}>
            {isEditMode
              ? `Edit ${
                  isManagerMode ? "Manager" : isStaffMode ? "Staff" : "Merchant"
                }`
              : `Add ${
                  isManagerMode ? "Manager" : isStaffMode ? "Staff" : "Merchant"
                }`}
          </Title>
        </Space>

        <StyledForm
          form={form}
          layout="vertical"
          onFinish={handleSave}
          onValuesChange={() => setIsUnsavedChanges(true)}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <StyledInput placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: "Username is required" }]}
          >
            <StyledInput placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <StyledInput type="email" placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: "Phone number is required" }]}
          >
            <StyledInput placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: "Address is required" }]}
          >
            <StyledTextArea rows={3} placeholder="Enter address" />
          </Form.Item>

          {!isEditMode && (
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: "Password is required" }]}
            >
              <StyledPasswordInput placeholder="Enter password" />
            </Form.Item>
          )}

          <Form.Item>
            <FormButtonContainer>
              <Button onClick={handleCancel}>Cancel</Button>
              <SaveButton
                type="primary"
                htmlType="submit"
                loading={loading.create || loading.update}
                icon={<FontAwesomeIcon icon={faSave} />}
              >
                Save
              </SaveButton>
            </FormButtonContainer>
          </Form.Item>
        </StyledForm>
      </Space>

      <StyledModal
        title="Unsaved Changes"
        open={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        footer={[
          <Button
            key="no"
            onClick={() => setIsCancelModalOpen(false)}
            style={{
              borderColor: "#d9d9d9",
              color: "#595959",
            }}
          >
            No
          </Button>,
          <Button
            key="yes"
            type="primary"
            danger
            onClick={confirmCancel}
            style={{
              background: "#ff4d4f",
              borderColor: "#ff4d4f",
            }}
          >
            Yes
          </Button>,
        ]}
      >
        <p>You have unsaved changes. Are you sure you want to cancel?</p>
      </StyledModal>
    </>
  );
};

export default MerchantBox;